import { Helmet } from "react-helmet-async";
import {
  SITE_URL,
  SITE_NAME,
  DEFAULT_DESCRIPTION,
} from "../../config/siteConfig";

/**
 * Build absolute image URL from product image (string or object).
 */
function toImageUrl(img) {
  if (!img) return null;
  const path = typeof img === "string" ? img : img?.src ?? null;
  if (!path) return null;
  return path.startsWith("http") ? path : `${SITE_URL}${path}`;
}

/**
 * JSON-LD structured data for Organization.
 */
export function OrganizationJsonLd() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: SITE_NAME,
    url: SITE_URL,
    logo: `${SITE_URL}/companylogo2.png`,
    contactPoint: {
      "@type": "ContactPoint",
      telephone: "+852-98312915",
      contactType: "customer service",
      areaServed: "HK",
      availableLanguage: ["Chinese", "English"],
    },
    sameAs: [
      "https://www.facebook.com/pages/category/Food-Wholesaler/貓山王朝-387798108690714",
    ],
  };
  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
}

/**
 * JSON-LD structured data for LocalBusiness (Hong Kong durian wholesale/retail).
 */
export function LocalBusinessJsonLd() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `${SITE_URL}/#localbusiness`,
    name: SITE_NAME,
    url: SITE_URL,
    logo: `${SITE_URL}/companylogo2.png`,
    description: DEFAULT_DESCRIPTION,
    address: {
      "@type": "PostalAddress",
      addressLocality: "九龍",
      streetAddress: "青山道479至479A號麗昌工廠大廈2樓202 P室",
      addressRegion: "Hong Kong",
      addressCountry: "HK",
    },
    areaServed: {
      "@type": "GeoCircle",
      geoMidpoint: {
        "@type": "GeoCoordinates",
        latitude: 22.3193,
        longitude: 114.1694,
      },
      geoRadius: "50000",
    },
    contactPoint: {
      "@type": "ContactPoint",
      telephone: "+852-98312915",
      contactType: "customer service",
      areaServed: "HK",
      availableLanguage: ["Chinese", "English"],
    },
    sameAs: [
      "https://www.facebook.com/pages/category/Food-Wholesaler/貓山王朝-387798108690714",
    ],
  };
  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
}

/**
 * JSON-LD structured data for WebSite.
 */
export function WebSiteJsonLd() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: SITE_NAME,
    url: SITE_URL,
    description: DEFAULT_DESCRIPTION,
  };
  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
}

/**
 * JSON-LD BreadcrumbList schema.
 * @param {Array<{name: string, path: string}>} items - Breadcrumb items
 */
export function BreadcrumbJsonLd({ items }) {
  if (!items?.length) return null;
  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, idx) => ({
      "@type": "ListItem",
      position: idx + 1,
      name: item.name,
      item: item.path.startsWith("http") ? item.path : `${SITE_URL}${item.path}`,
    })),
  };
  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
}

/**
 * JSON-LD structured data for a Product page.
 */
export function ProductJsonLd({ product }) {
  const productUrl = product?.url_link
    ? `${SITE_URL}/${product.url_link}`
    : SITE_URL;
  const imageUrls = (product?.image || [])
    .map(toImageUrl)
    .filter(Boolean);
  const primaryImage = imageUrls[0] || undefined;
  const description =
    product?.meta_description ||
    product?.title ||
    "";

  const schema = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product?.title || "",
    description,
    url: productUrl,
    image: imageUrls.length > 1 ? imageUrls : primaryImage,
    sku: product?.url_link || undefined,
    brand: {
      "@type": "Brand",
      name: SITE_NAME,
    },
    offers: {
      "@type": "Offer",
      url: productUrl,
      availability: product?.sold_out
        ? "https://schema.org/OutOfStock"
        : "https://schema.org/InStock",
    },
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
}
